import { useFormikContext } from 'formik';
import moment from 'moment';
import React, { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { OperationType } from 'screens/Applications&WorkOrders/RouteAdmin';
import {
  CropZone,
  FormValues,
  initialValues,
  RecordSourceType
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import {
  DEFAULT_OPERATION_DURATION_HOURS,
  DEFAULT_OPERATION_DURATION_MINUTES
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/defaultDuration';
import { useAuth } from 'utilities/base-auth';
import useProduct from 'hooks/useProduct';
import useUomConvert from 'hooks/useUomConvert';
import useMapProducts from 'screens/Applications&WorkOrders/Components/hooks/useMapProducts';
import useMapCropZones from 'screens/Applications&WorkOrders/Components/hooks/useMapCropZones';
import { enrichProductsWithListPrice } from 'screens/Applications&WorkOrders/Components/helpers/enrichProductsWithListPrice';
import {
  transformRecommendationCropZonesToOperationPropertyVO,
  transformRecommendationCropZonesToProperty,
  transformRecommendationProductEntryToProduct,
  enrichApplicatorsAndAuthorizer
} from 'screens/Applications&WorkOrders/Components/CreateOperationFromRecommendation/helpers';
import { RecommendationVO } from 'screens/Applications&WorkOrders/Components/CreateOperationFromRecommendation/types';
import { useAgOperationUtility } from 'screens/Applications&WorkOrders/StepsLayout/Context/AgOperationUtilityProvider';
import generateDefaultApplicationId from 'screens/Applications&WorkOrders/StepsLayout/Helpers/generateDefaultApplicationId';

const useConversionRecommendationToOperation = () => {
  const location = useLocation<{
    recommendation: RecommendationVO;
    targetOperation: OperationType;
  }>();
  const { recommendation: recommendationState, targetOperation } =
    location.state || {};
  const { updateUtilityState } = useAgOperationUtility();
  const { setValues } = useFormikContext<FormValues>();
  const { user } = useAuth();
  const { getProduct } = useProduct();
  const convertFn = useUomConvert();
  const { buildProductsDataMap, fetchAllPests, mapProducts } = useMapProducts();
  const {
    propertyLandingPageData,
    fetchCropZoneSeasons,
    buildCropZoneMap,
    mapCropZones,
    isPropertiesMapLoading: isMapDataLoading
  } = useMapCropZones();
  const [isLoading, setIsLoading] = React.useState(false);

  const mapCropZonesData = useCallback(async (): Promise<CropZone[]> => {
    if (!recommendationState?.cropZones?.length) {
      return [];
    }

    const operationPropertyVOs = transformRecommendationCropZonesToOperationPropertyVO(
      recommendationState,
      propertyLandingPageData
    );
    const properties = transformRecommendationCropZonesToProperty(
      recommendationState,
      propertyLandingPageData
    );

    if (!operationPropertyVOs.length || !properties.length) {
      return [];
    }

    const selectedCropSeasonIds = JSON.parse(
      localStorage.getItem('selectedCropSeasons') ?? '[]'
    );
    const cropSeasonIdsToUse = recommendationState.cropSeasonIds.filter(id =>
      selectedCropSeasonIds.includes(id)
    );

    const cropZoneSeasonsMap = await fetchCropZoneSeasons(cropSeasonIdsToUse);
    const cropZoneMapProp = buildCropZoneMap(properties);
    return mapCropZones(
      cropZoneMapProp,
      cropZoneSeasonsMap,
      operationPropertyVOs,
      RecordSourceType.RECOMMENDATION
    );
  }, [
    recommendationState,
    propertyLandingPageData,
    fetchCropZoneSeasons,
    buildCropZoneMap,
    mapCropZones
  ]);

  const mapProductsData = useCallback(
    async (totalAppliedArea: number): Promise<any[]> => {
      if (!recommendationState?.recommendationProductEntries?.length) {
        return [];
      }

      const products = recommendationState.recommendationProductEntries.map(
        transformRecommendationProductEntryToProduct
      );

      const productsFullDataMap = await buildProductsDataMap(products);
      const allPests = await fetchAllPests(products);
      const mappedProducts = mapProducts(
        products,
        productsFullDataMap,
        allPests,
        totalAppliedArea
      );

      // A Recommendation has no cost of its own, so seed each product with its
      // saved list price and resulting total cost (default behaviour).
      return enrichProductsWithListPrice(mappedProducts, getProduct, convertFn);
    },
    [
      recommendationState,
      buildProductsDataMap,
      fetchAllPests,
      mapProducts,
      getProduct,
      convertFn
    ]
  );

  const convertRecommendationToOperationFormikData = useCallback(async (): Promise<
    void
  > => {
    setIsLoading(true);

    try {
      if (!recommendationState) {
        setIsLoading(false);
        return;
      }

      const startDateTime = moment(
        recommendationState.proposedDateEpoch || new Date()
      )
        .startOf('day')
        .hour(8);

      // Use authorization date if available, otherwise use proposed date
      const authorizationDateTime = recommendationState.authorisedOnEpoch
        ? moment(recommendationState.authorisedOnEpoch)
        : startDateTime;

      const {
        enrichedApplicators,
        authorizedBy
      } = await enrichApplicatorsAndAuthorizer(recommendationState);

      const mappedCropZones = await mapCropZonesData();

      const totalAppliedArea =
        mappedCropZones.reduce(
          (sum, cz) => sum + (cz.appliedAcreage || 0),
          0
        ) || 0;
      const mappedProducts = await mapProductsData(totalAppliedArea);

      // Build form values inline
      const initials =
        user?.name && user.name.trim().length > 0
          ? user.name
              .split(' ')
              .map((n: string) => n[0])
              .join('')
          : 'NA';

      const endDateTime = startDateTime
        .clone()
        .add(DEFAULT_OPERATION_DURATION_HOURS, 'hours');

      const newValues: FormValues = {
        ...initialValues,
        id: undefined,
        startDateTime,
        ...(targetOperation === OperationType.APPLICATION
          ? {
              endDateTime,
              durationHours: DEFAULT_OPERATION_DURATION_HOURS,
              durationMinutes: DEFAULT_OPERATION_DURATION_MINUTES
            }
          : { weather: undefined }),
        applicationId: generateDefaultApplicationId(initials),
        authorizationDate: authorizationDateTime ?? startDateTime,
        authorizedBy,
        notes: recommendationState.notes || '',
        tagIds: recommendationState.flagIds || [],
        applicators: enrichedApplicators.length
          ? enrichedApplicators
          : initialValues.applicators,
        cropZones: mappedCropZones,
        products: mappedProducts,
        totalAppliedAcreage: totalAppliedArea,
        applicationMethod: recommendationState.applicationMethod || '',
        timingEvent: recommendationState.timingEvent || '',
        recordSource: {
          recordSourceType: RecordSourceType.RECOMMENDATION,
          recordSourceId: recommendationState.id,
          recordSourceName: recommendationState.title || ''
        }
      };

      setValues(newValues);

      if (recommendationState.tankInformation) {
        const {
          tankCount,
          tankSizeValue,
          totalCarrierValue,
          carrierPerAreaValue
        } = recommendationState.tankInformation;

        updateUtilityState({
          showTankInfoBanner:
            tankCount !== 0 &&
            tankSizeValue !== 0 &&
            totalCarrierValue !== 0 &&
            carrierPerAreaValue !== 0
        });
      } else {
        updateUtilityState({ showTankInfoBanner: false });
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    targetOperation,
    recommendationState,
    user,
    mapCropZonesData,
    mapProductsData,
    setValues,
    updateUtilityState
  ]);

  return {
    convertRecommendationToOperationFormikData,
    isLoading: isMapDataLoading || isLoading,
    targetOperation
  };
};

export default useConversionRecommendationToOperation;
